/* eslint-disable no-nested-ternary */
import { CheckCircle, StarBorder } from '@mui/icons-material';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { PropsWithChildren, useCallback } from 'react';
import { Cafe } from '../../apiTypes';
import { StyledPaper } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { updateDoc } from '../../utils/api';

type Props = {
  name: 'FREE' | 'LITE' | 'PLUS' | 'PRO';
  discountedPrice?: number;
  price: number;
  yearPrice: number;
  isSelected?: boolean;
  lastDate?: number | null;
  submitTitle?: string | boolean;
  onSubmit?: () => void;
  isRed?: boolean;
};

const PlanCard = ({
  name,
  price,
  discountedPrice,
  isSelected = false,
  yearPrice,
  lastDate = 0,
  submitTitle,
  onSubmit,
  children,
  isRed,
}: PropsWithChildren<Props>) => {
  const { lang } = useLocale();
  const { cafe } = useAdmin();

  return (
    <StyledPaper
      sx={{
        position: 'relative',
        p: 2,
        background: (t) => t.palette.background.paper,
        height: 'calc(100% - 36px)',
      }}
    >
      {isSelected && (
        <Chip
          icon={<CheckCircle />}
          sx={{ position: 'absolute', top: 5, left: 5 }}
          label={
            name === 'FREE'
              ? t.current_plan[lang]
              : `${t.valid_until[lang]}: ${format(lastDate || new Date(), 'dd.MM.yyyy')}`
          }
          color={name !== 'FREE' && (lastDate || 0) > Date.now() ? 'success' : 'error'}
          size="small"
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
        <div>
          <Typography sx={{ mb: 2, mt: 4 }} variant="h5" align="center">
            {name}
          </Typography>

          {children}

          {/* <Divider />

          <Box sx={{ my: 1 }}>
            <Typography variant="h6" align="center" color={(t) => t.palette.text.secondary}>
              {discountedPrice && (
                <Typography component="span" sx={{ textDecoration: 'line-through' }}>
                  {`${currency(discountedPrice * 100, cafe?.currency, true, 0)}/${t.month[lang]}`}
                </Typography>
              )}
              {` ${currency(price * 100, cafe?.currency, true, 0)}/${t.month[lang]}`}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} /> */}
        </div>

        {/* {!isSelected && submitTitle && (
          <div>
            <PrimaryButton fullWidth sx={{ mt: 2 }} onClick={onSubmit} color="inherit">
              {submitTitle}
            </PrimaryButton>
          </div>
        )}

        {isSelected && <PayForPlanButton />} */}
      </Box>
    </StyledPaper>
  );
};

const Plan = () => {
  const { cafe, cafeSlug, isPlus, isPro, isLite } = useAdmin();
  const currency = cafe?.currency || '₴';
  const { lang } = useLocale();

  const selectPlan = useCallback(
    (newPlan: 'lite' | 'plus' | 'pro') => {
      let newLastDate: number = cafe?.plan?.lastDate || new Date().getTime();
      const diff = cafe?.plan?.lastDate ? differenceInCalendarDays(cafe.plan.lastDate, new Date().getTime()) : 0;

      if (cafe?.plan?.lastDate && diff > 0) {
        if (cafe.plan.type === 'free' || cafe.plan.type === 'lite') {
          if (newPlan === 'plus') {
            newLastDate = addDays(new Date(), diff / 3).getTime();
          }
          if (newPlan === 'pro') {
            newLastDate = addDays(new Date(), diff / 6).getTime();
          }
        }

        if (cafe.plan.type === 'plus') {
          if (newPlan === 'lite') {
            newLastDate = addDays(new Date(), diff * 3).getTime();
          }
          if (newPlan === 'pro') {
            newLastDate = addDays(new Date(), diff / 2).getTime();
          }
        }

        if (cafe.plan.type === 'pro') {
          if (newPlan === 'lite') {
            newLastDate = addDays(new Date(), diff * 6).getTime();
          }
          if (newPlan === 'plus') {
            newLastDate = addDays(new Date(), diff * 2).getTime();
          }
        }
      }

      updateDoc<Cafe>(`cafes/${cafeSlug}`, {
        'plan.type': newPlan,
        'plan.lastDate': newLastDate,
      });
    },
    [cafe?.plan?.lastDate, cafe?.plan?.type, cafeSlug]
  );

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <PlanCard
            name="LITE"
            price={currency === '₴' ? (!cafe?.plan || cafe?.plan?.type === 'free' ? 300 : 500) : 33}
            yearPrice={currency === '₴' ? (!cafe?.plan || cafe?.plan?.type === 'free' ? 3600 : 6000) : 33}
            discountedPrice={currency === '₴' && (!cafe?.plan || cafe?.plan?.type === 'free') ? 500 : undefined}
            isSelected={!cafe?.plan?.type || cafe?.plan?.type === 'free' || cafe?.plan?.type === 'lite'}
            lastDate={cafe?.plan?.lastDate || 1709247600000}
            submitTitle={!isPro && !isPlus && `${t.switch_to[lang]} LITE`}
            onSubmit={() => selectPlan('lite')}
            isRed={(cafe?.plan?.type === 'free' || cafe?.plan?.type === 'lite') && !isLite}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4, height: 30 }}>
              <StarBorder />
            </Box>
          </PlanCard>
        </Grid>

        <Grid item xs={12} sm={4}>
          <PlanCard
            name="PLUS"
            price={currency === '₴' ? 1500 : 100}
            yearPrice={currency === '₴' ? 18000 : 1200}
            lastDate={cafe?.plan?.lastDate}
            isSelected={cafe?.plan?.type === 'plus'}
            submitTitle={!isPro && `${t.switch_to[lang]} PLUS`}
            onSubmit={() => selectPlan('plus')}
            isRed={cafe?.plan?.type === 'plus' && !isPlus}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4, height: 30 }}>
              <StarBorder />
              <StarBorder fontSize="large" />
              <StarBorder />
            </Box>
          </PlanCard>
        </Grid>

        <Grid item xs={12} sm={4}>
          <PlanCard
            name="PRO"
            discountedPrice={currency === '₴' ? 3000 : 200}
            price={currency === '₴' ? 1900 : 149}
            yearPrice={currency === '₴' ? 22800 : 1788}
            isSelected={cafe?.plan?.type === 'pro'}
            lastDate={cafe?.plan?.lastDate}
            submitTitle={`${t.switch_to[lang]} PRO`}
            onSubmit={() => selectPlan('pro')}
            isRed={cafe?.plan?.type === 'pro' && !isPro}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4, height: 30 }}>
              <StarBorder fontSize="small" />
              <StarBorder />
              <StarBorder fontSize="large" />
              <StarBorder />
              <StarBorder fontSize="small" />
            </Box>
          </PlanCard>
        </Grid>
      </Grid>

      <Box sx={{ my: 3, textAlign: 'center' }}>
        <Typography color="secondary">{t.tariff_plans_details[lang]}</Typography>
      </Box>
    </Box>
  );
};

export default Plan;
